<script lang="ts">
  /* THE HOSTED CHART — which is now a registry and four snippets.
   *
   * ═══ WHAT THIS FILE USED TO BE ═══
   *
   * 292 lines, of which about 180 were layout: a LAYERS button at `top: 8px;
   * left: 8px`, a theme button at `top: 78px`, a cache button at `top: 116px`
   * (130 on a coarse pointer), a `.cachewrap` at `top: 8px; right: 8px`, three
   * hand-mounted `MobileSheet`s with their own peek snippets, and a
   * `--gd-panel-inset` published on `<main>` so that a layer bar and a scrubber
   * in a different component tree could re-centre themselves on what the panel
   * left over.
   *
   * Every one of those numbers was an unenforced claim about some other
   * element, and the history of this app is one of them landing on another —
   * MEASURE drawn on top of CACHE, the layer bar pinned to the right of every
   * desktop for a release, the time control 177 px off the right edge in
   * production with the whole proof suite green.
   *
   * All of it is gone. `<AppShell>` owns the arrangement, the panel registry
   * owns what exists, and this file owns the two things that are genuinely this
   * deployment's business: WHICH panels, and what the chrome around the chart
   * says.
   *
   * ═══ ADDING A PANEL IS ONE REGISTRATION ═══
   *
   * It used to cost four edits in four files: mount a sheet here, add an id to
   * `mobile.svelte.ts`, add a slot to `Dock.svelte`, and write the desktop
   * branch separately. That is why `Layers.svelte` had an `inSheet` prop,
   * `ChartTools.svelte` had a `variant` prop, and `RouteContent.svelte` existed
   * at all — three ad-hoc answers to one missing abstraction.
   */
  import { onMount } from 'svelte';
  import TapDebug from './TapDebug.svelte';
  import AdminPage from '@gennetay/chart-ui/AdminPage';
  import AppShell from '@gennetay/chart-ui/shell/AppShell';
  import Tideline from '@gennetay/chart-ui/Tideline';
  import Button from '@gennetay/chart-ui/primitives/Button';
  import Chip from '@gennetay/chart-ui/primitives/Chip';
  import Icon from '@gennetay/chart-ui/primitives/Icon';
  import { layout, panels } from '@gennetay/chart-ui/shell';

  import { theme } from '@gennetay/chart-core/lib/theme.svelte';
  import { layers } from '@gennetay/chart-core/map/layers.svelte';
  import { autoroute } from '@gennetay/chart-core/lib/autoroute.svelte';
  import { editor } from '@gennetay/chart-core/lib/editor.svelte';
  import { routePlan } from '@gennetay/chart-core/lib/routeplan.svelte';
  import { trips } from '@gennetay/chart-core/lib/trip.svelte';
  import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';
  import { gatesFromStations } from '@gennetay/chart-core/lib/currents';
  import { fmtEte, fmtNm } from '@gennetay/chart-core/lib/nav';
  import { routeDistanceM } from '@gennetay/chart-core/lib/routesummary';
  import { currents } from '@gennetay/chart-layers/packs/tides/currents.svelte';

  import Chart from './Chart.svelte';
  import Credit from './Credit.svelte';
  import Scale from './Scale.svelte';
  import ZoomIndicator from './ZoomIndicator.svelte';
  import { armedLabel, disarmAll } from './toollabels.svelte';
  import { themeTitle } from './themeTitle';
  import { clearPickedUnlessOpen, picked } from './pick.svelte';
  import { pulse } from './pulse.svelte';
  import { planRoute, resolveRoute } from './route.svelte';
  import { departures } from './departures.svelte';
  /* Aliased: `tideline` is also the name of AppShell's snippet prop below, and
     a snippet shadows a module import inside the template. */
  import { tideline as tidelineState } from './tideline.svelte';

  import CachePanelBody from './panels/CachePanel.svelte';
  import DeparturePanel from './panels/DeparturePanel.svelte';
  import LayersPanel from './panels/LayersPanel.svelte';
  import ObjectPanel from './panels/ObjectPanel.svelte';
  import SearchPanel from './panels/SearchPanel.svelte';
  import TimePanel from './panels/TimePanel.svelte';
  import ToolsPanel from './panels/ToolsPanel.svelte';
  import TripFoot from './panels/TripFoot.svelte';
  import TripPanel from './panels/TripPanel.svelte';
  import TripsPanel from './panels/TripsPanel.svelte';

  import type { Map as MlMap } from 'maplibre-gl';

  /* ═══ ONE ROUTE, AND NO ROUTER ═══
   * /admin is the only path this app has besides /. A router would be more
   * machinery than the thing it routes. `not_found_handling:
   * single-page-application` in wrangler.jsonc serves index.html for it, so a
   * deep link and a reload both land here. */
  const admin = location.pathname.replace(/\/+$/, '') === '/admin';

  let map = $state<MlMap | null>(null);

  const hasRoute = $derived(!!(autoroute.result || autoroute.unsolvable || autoroute.error));
  const listed = $derived((void pulse.n, layers.list().filter((m) => m.listed !== false).length));
  const onCount = $derived(
    (void pulse.n, layers.list().filter((m) => m.listed !== false && layers.isVisible(m.id)).length)
  );
  /* #120: the reason is the STATE only — the rail/dock accessible name becomes
     "Trip — No route yet." — and the how-to lives in `detail`, which renders
     in the panel's Empty explanation one tap away. */
  const noRouteReason = 'No route yet.';
  const noRouteDetail =
    'Open Tools, tap the route tool, and tap two points on the chart.';
  /* #91: Depart names its own job — a route comes first (Trip), then the
     departure choice. Restores the distinct Depart wording from #17. */
  const noDepartReason = 'No route yet.';
  const noDepartDetail = 'Plan a route first (Trip), then choose a departure.';

  /* ═══ THE MARK AND THE WORD, FROM THE MODE ═══
   *
   * NOT from `theme.label`, which returns "☼ AUTO" — a glyph baked into a
   * string by a store in chart-core. Rendering that beside an <Icon> drew the
   * sun twice. It is left alone because apps/dash reads it and the boat is not
   * this task's to change; the mode is public and says the same thing without
   * an opinion about how it looks. */
  const themeIcon = $derived(
    theme.mode === 'auto' ? 'auto' : theme.mode === 'day' ? 'day' : 'night'
  );
  const themeWord = $derived(theme.mode.toUpperCase());
  /* #24: the pill shows only the state word — the tooltip + accessible name
   * carry the control's name, the mode's meaning, and the tap-to-cycle action. */
  const themeTip = $derived(themeTitle(theme.mode));

  const hhmm = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  /* ═══ THE GATES THE TIDELINE DRAWS ═══
   *
   * The narrows inside the chart you are looking at, gated on the boat's own
   * speed — a 12 kn launch is not gated by Active Pass at all and should not be
   * shown a band pretending otherwise. Twenty-odd BC gates drawn at once is a
   * wall of stripes that means nothing; the ones you can see are the ones you
   * are deciding about.
   *
   * `gatesFromStations` is the ROUTER'S own, so the picture and the plan cannot
   * disagree about when a pass is open. */
  let viewTick = $state(0);
  const gates = $derived.by(() => {
    void viewTick;
    const b = map?.getBounds();
    const all = gatesFromStations(currents.stations, autoroute.boatKn);
    if (!b) return [];
    const c = b.getCenter();
    return all
      .filter(
        (g) =>
          g.lat >= b.getSouth() &&
          g.lat <= b.getNorth() &&
          g.lon >= b.getWest() &&
          g.lon <= b.getEast()
      )
      .map((g) => ({ g, d: (g.lat - c.lat) ** 2 + (g.lon - c.lng) ** 2 }))
      .sort((p, q) => p.d - q.d)
      .map(({ g }) => g);
  });

  /** What the tideline's trip bar draws, or null. */
  const tripBar = $derived.by(() => {
    const s = autoroute.schedule;
    if (!s) return null;
    return {
      departT: s.departT,
      arriveT: s.arriveT,
      holds: s.waits.map((w) => ({ from: w.from, untilT: w.untilT, name: w.gate.name }))
    };
  });

  /* ═══ THE REGISTRY ═══
   *
   * Nine entries, in the order a passage is actually planned: where am I going,
   * when should I leave, where is that place, what have I saved, what is the
   * water doing, what can I do to the chart, what did I just tap, and — last,
   * because it is a diagnostic and not a chart control — the tile cache. */
  onMount(() => {
    const off = [
      panels.register({
        id: 'trip',
        title: 'Trip',
        icon: 'trip',
        order: 10,
        content: TripPanel,
        foot: TripFoot,
        /* The ANSWER. A wait is the most surprising thing a current-aware route
         * can tell you, so it leads when there is one. */
        peek: () => {
          const s = autoroute.schedule;
          if (s && s.waits.length) {
            const w = s.waits[0]!;
            return `hold ${fmtEte((w.untilT - w.from) / 1000)} at ${w.gate.name}`;
          }
          if (autoroute.unsolvable) return 'No route';
          if (autoroute.error) return 'Route failed';
          const d = routeDistanceM(editor.pts);
          const arrive = s ? ` · arrive ${hhmm(s.arriveT)}` : '';
          return `${d === null ? '—' : `${fmtNm(d)} nm`}${arrive}`;
        },
        available: () => hasRoute,
        reason: () => (hasRoute ? null : noRouteReason),
        detail: () => (hasRoute ? null : noRouteDetail),
        unavailableAction: planRouteAction
      }),
      panels.register({
        id: 'departure',
        title: 'Depart',
        icon: 'depart',
        order: 20,
        content: DeparturePanel,
        peek: () => {
          const best = departuresBest();
          return best ?? 'When to leave';
        },
        available: () => hasRoute,
        reason: () => (hasRoute ? null : noDepartReason),
        detail: () => (hasRoute ? null : noDepartDetail),
        unavailableAction: planRouteAction
      }),
      panels.register({
        id: 'search',
        title: 'Search',
        icon: 'search',
        order: 30,
        content: SearchPanel,
        peek: () => 'Stations, anchorages, coordinates'
      }),
      panels.register({
        id: 'trips',
        title: 'Trips',
        icon: 'trips',
        order: 40,
        content: TripsPanel,
        peek: () => (trips.list.length ? `${trips.list.length} saved` : 'Nothing saved yet')
      }),
      panels.register({
        id: 'layers',
        title: 'Layers',
        icon: 'layers',
        order: 50,
        content: LayersPanel,
        /* The answer: how much is switched on, not the word "layers". */
        peek: () => `${onCount} of ${listed} layers on`
      }),
      panels.register({
        id: 'time',
        title: 'Time',
        order: 60,
        content: TimePanel,
        icon: 'time',
        /* The READING replaces the mark, and amber is this app's one word for
         * "this is not now" — a parked cursor means every tide bar, stream
         * arrow and current field on the chart behind it is drawn for another
         * moment. Nielsen Norman are explicit that always-needed state must not
         * live behind a disclosure, which is what `pin` is for. */
        readout: () => (void pulse.n, viewTime.isLive ? 'Live' : hhmm(viewTime.now)),
        tone: () => (viewTime.isLive ? 'normal' : 'warn'),
        pin: true,
        peek: () => (viewTime.isLive ? 'Showing now' : `Showing ${hhmm(viewTime.now)}`)
      }),
      panels.register({
        id: 'tools',
        title: 'Tools',
        order: 70,
        content: ToolsPanel,
        icon: 'tools',
        /* ═══ THE LABEL IS THE STATE ═══
         * "TAP TWO POINTS" is not something a chart can imply, and an armed
         * tool with no indicator is a mode you cannot see. While one is armed
         * the entry shows it and tapping stands it down; otherwise it is the
         * ruler, and the way in to the list. */
        readout: () => armedLabel(),
        /* Pinned only while armed. A mode whose only indicator is behind a MORE
           menu is a mode you cannot see. */
        pin: () => armedLabel() !== null,
        onactivate: () => {
          if (!armedLabel()) return false;
          disarmAll();
          return true;
        },
        peek: () => armedLabel() ?? 'Route, measure and 3D'
      }),
      panels.register({
        id: 'pick',
        title: 'Chart object',
        icon: 'pick',
        order: 80,
        content: ObjectPanel,
        peek: () => picked.peek(),
        /* Opens in RESPONSE to a tap rather than being navigated to, so it gets
         * no rail button — a permanent entry for it would be one that does
         * nothing until it does. */
        nav: false,
        available: () => picked.hits.length > 0
      }),
      panels.register({
        id: 'cache',
        title: 'Cache',
        icon: 'cache',
        order: 90,
        content: CachePanelBody,
        peek: () => 'Tile cache'
      })
    ];
    return () => off.forEach((fn) => fn());
  });

  /** The best departure, for the panel's peek line. Read from the store rather
   *  than recomputed, so the two cannot disagree. */
  function departuresBest(): string | null {
    const o = departures.options[0];
    if (!o || o.elapsedMs === null) return null;
    return `Best ${hhmm(o.departT)} · ${fmtEte(o.elapsedMs / 1000)}`;
  }

  /* ═══ A SOLVED ROUTE RAISES THE TRIP PANEL — ONCE ═══
   *
   * At peek on a phone and not open, deliberately: the user was looking at the
   * chart when they tapped the second point, and the answer fits on one line.
   * Opening it would take the water away to show them something they can
   * already read. On a desktop the panel column is already there, so it fills.
   *
   * ═══ ONCE, AND THAT WORD IS LOAD-BEARING ═══
   *
   * This was `if (hasRoute && panels.open !== 'trip') panels.show('trip')`,
   * which is not "raise the panel when a route solves" — it is "the trip panel
   * should always be open", a standing rule the reactive system re-enforces
   * every time anything changes. Opening Search with a route on the chart
   * bounced straight back to Trip and there was no way to reach any other
   * panel at all.
   *
   * The same mistake, in the same file, that the object pick had: a
   * CONSEQUENCE OF AN EVENT written as a STANDING TRUTH. Tracking the edge —
   * "there was no route, now there is" — is what makes it an event. */
  let hadRoute = false;
  $effect(() => {
    const now = hasRoute;
    if (now && !hadRoute) panels.show('trip', layout.phone ? 'peek' : 'open');
    hadRoute = now;
  });

  /* ═══ THE PICK GIVES THE SURFACE BACK ═══
   *
   * Opening it is a consequence of the TAP and is done there — see
   * `Chart.svelte`. It was an effect here, `if (picked.hits.length &&
   * panels.open !== 'pick') panels.show('pick')`, which is a standing rule
   * rather than a one-shot: it re-opened the pick every time the user chose any
   * other panel, so tapping a station and then LAYERS bounced straight back.
   * `_mobile-proof.mjs` found it by asking for the tools panel and being handed
   * the object inspector.
   *
   * This half stays, and is the opposite thing: when something else claims the
   * surface the hit list goes with it, or the old one returns the next time
   * this panel opens for an unrelated reason. */
  $effect(() => {
    const pickOpen = panels.is('pick');
    /* The panel selection is the trigger. The hit list is the state this
     * effect disposes, so tracking it as an input makes the effect subscribe
     * to its own array replacement and schedules a second pass in the same
     * flush. Keep disposal outside the dependency graph. */
    clearPickedUnlessOpen(pickOpen);
  });

  // TEMPORARY DIAGNOSTIC: expose panels for tap debug overlay
  onMount(() => {
    (window as any).__panels = panels;
  });
</script>

{#snippet planRouteAction()}
  <Button intent="primary" onclick={planRoute}>Plan a route</Button>
{/snippet}

{#if admin}
  <AdminPage />
{:else}
  <div class:night={theme.night} style="display: contents">
    <AppShell>
      {#snippet chart()}
        <Chart
          onmap={(m) => (map = m)}
          onmove={() => viewTick++}
          ontick={() => pulse.bump()}
        />

        <!-- ═══ ONE ROW, SO NEITHER END DESCRIBES THE OTHER ═══
             The credit and the zoom pair were two floats anchored independently
             to the same corner, and they landed on each other — the same fault,
             at 1/50th the scale, as the eight overlays this phase deleted. As a
             flex row with `space-between` there is nothing left to be wrong
             about. -->
        <!-- ═══ THE ZOOM PAIR IS DOCK FURNITURE ON A PHONE ═══
             At the chart's bottom-right corner it sits under every sheet, which
             is precisely what the dock was built to stop: the one control that
             must always be reachable cannot live where a panel comes up. On a
             desktop nothing covers that corner, so it stays on the chart. -->
        <div class="float bottom">
          {#if !layout.phone}
            <div class="creditwrap">
              <Credit>
                {#snippet lead()}<Scale {map} /><ZoomIndicator {map} />{/snippet}
              </Credit>
            </div>
          {:else}
            <span></span>
          {/if}
          {#if !layout.phone}
          <div class="zoom">
            <!-- Zoom is a first-class control, not a fallback. chart-ui hides
                 MapLibre's own on a coarse pointer on the reasoning that pinch
                 is the gesture people already use; on a boat it is the gesture
                 that fails FIRST — salt water is a grounded conductive blob, so
                 a wave breaking over the screen reads as multi-touch and the
                 chart starts panning by itself. A button cannot be misread by
                 spray. -->
            <button
              aria-label="zoom in"
              onclick={() => map?.easeTo({ zoom: (map.getZoom() ?? 0) + 1, duration: 220 })}
            ><Icon name="zoom-in" size={20} /></button>
            <button
              aria-label="zoom out"
              onclick={() => map?.easeTo({ zoom: (map.getZoom() ?? 0) - 1, duration: 220 })}
            ><Icon name="zoom-out" size={20} /></button>
          </div>
          {/if}
        </div>
      {/snippet}

      {#snippet dockTools()}
        <div class="zoom row">
          <button
            aria-label="zoom out"
            onclick={() => map?.easeTo({ zoom: (map.getZoom() ?? 0) - 1, duration: 220 })}
          ><Icon name="zoom-out" size={20} /></button>
          <button
            aria-label="zoom in"
            onclick={() => map?.easeTo({ zoom: (map.getZoom() ?? 0) + 1, duration: 220 })}
          ><Icon name="zoom-in" size={20} /></button>
        </div>
      {/snippet}

      {#snippet commandbar()}
        <div class="cbar">
          <span class="brand">Gennetay</span>
          <span class="right">
            {#if !viewTime.isLive}
              <Chip tone="warn">Parked {(void pulse.n, hhmm(viewTime.now))}</Chip>
            {/if}
            {#if armedLabel()}
              <button class="armed" onclick={disarmAll}>{armedLabel()}</button>
            {/if}
            <!-- Auto → day → night → auto, flipping at 19:00 and 07:00 local.
                 Blunt on purpose: a real sunset calculation drifts with latitude
                 and season and would flip the screen mid-passage, where a fixed
                 pair of hours is predictable and a crew learns it. -->
            <!-- The mark says which mode, the label says its name. Auto → day →
                 night → auto, flipping at 19:00 and 07:00 local — blunt on
                 purpose: a real sunset calculation drifts with latitude and
                 season and would flip the screen mid-passage, where a fixed
                 pair of hours is predictable and a crew learns it. -->
            <Button onclick={() => theme.cycle()} title={themeTip} ariaLabel={themeTip}>
              <Icon name={themeIcon} size={16} />
              {themeWord}
            </Button>
          </span>
        </div>
      {/snippet}

      {#snippet toprail()}
        <span class="tscale"><Scale {map} /><ZoomIndicator {map} /></span>
        <Button icon title={themeTip} ariaLabel={themeTip} onclick={() => theme.cycle()}>
          <Icon name={themeIcon} size={18} />
        </Button>
      {/snippet}

      {#snippet tideline()}
        <Tideline
          {gates}
          trip={tripBar}
          tick={pulse.n}
          bind:collapsed={tidelineState.collapsed}
          compact={layout.phone}
          ondepart={(t) => {
            autoroute.departT = t;
            viewTime.scrub(t);
            if (routePlan.start && routePlan.end) void resolveRoute();
          }}
        />
      {/snippet}

      {#snippet fineprint()}
        <Credit inDock />
      {/snippet}

      {#snippet overlay()}
        <TapDebug />
      {/snippet}
    </AppShell>
  </div>
{/if}

<style>
  .float {
    position: absolute;
    z-index: 2;
    display: flex;
    gap: var(--sp-3);
  }
  .bottom {
    left: var(--sp-5);
    right: var(--sp-5);
    bottom: var(--sp-5);
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--sp-6);
    /* The row is a LAYOUT and the chart shows through it; each end opts back
       in. Without this the whole bottom strip of the chart stops taking a
       drag. */
    pointer-events: none;
  }
  .bottom > :global(*) {
    pointer-events: auto;
  }
  .creditwrap {
    min-width: 0;
    max-width: 62ch;
  }

  .tscale {
    color: var(--sub);
    font: var(--fs-micro) / 1.6 var(--mono);
    text-shadow: 0 1px 2px var(--ovl);
    pointer-events: none;
  }

  .zoom {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }
  /* In the dock it is a pair beside the slots, not a column above them. */
  .zoom.row {
    flex-direction: row;
    flex: none;
  }
  .zoom button {
    width: var(--h-touch);
    height: var(--h-touch);
    display: grid;
    place-items: center;
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    background: var(--ovl);
    backdrop-filter: blur(8px);
    color: var(--ink);
    font: 17px/1 var(--mono);
    cursor: pointer;
    box-shadow: var(--el-1);
  }

  .cbar {
    display: flex;
    align-items: center;
    gap: var(--sp-4);
    height: 100%;
    padding: 0 var(--sp-5);
  }
  .brand {
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
    color: var(--ink);
    text-transform: uppercase;
    padding-right: var(--sp-4);
    border-right: 1px solid var(--line);
  }
  .right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: var(--sp-4);
  }
  /* An armed tool with no indicator is a mode you cannot see, and this is the
     desktop's version of the dock's armed slot. Tapping it stands the tool
     down. */
  .armed {
    min-height: var(--h-md);
    padding: 0 var(--sp-5);
    border: 1px solid var(--cy);
    border-radius: var(--r-2);
    background: var(--cy-weak);
    color: var(--cy);
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
    cursor: pointer;
  }</style>
